// Figma file configuration
export const FIGMA_FILE_KEY = 'T1Evmt1n3rh8BubIbWrLbw';
export const getFigmaUrl = (nodeId: string) =>
  `https://www.figma.com/design/${FIGMA_FILE_KEY}/Lead-Management-Dashboard?node-id=${nodeId}`;

// Figma icon component
export const FigmaIcon = ({ size = 16 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M8 24C10.208 24 12 22.208 12 20V16H8C5.792 16 4 17.792 4 20C4 22.208 5.792 24 8 24Z"
      fill="#0ACF83"
    />
    <path
      d="M4 12C4 9.792 5.792 8 8 8H12V16H8C5.792 16 4 14.208 4 12Z"
      fill="#A259FF"
    />
    <path
      d="M4 4C4 1.792 5.792 0 8 0H12V8H8C5.792 8 4 6.208 4 4Z"
      fill="#F24E1E"
    />
    <path
      d="M12 0H16C18.208 0 20 1.792 20 4C20 6.208 18.208 8 16 8H12V0Z"
      fill="#FF7262"
    />
    <path
      d="M20 12C20 14.208 18.208 16 16 16C13.792 16 12 14.208 12 12C12 9.792 13.792 8 16 8C18.208 8 20 9.792 20 12Z"
      fill="#1ABCFE"
    />
  </svg>
);

// Section header component with optional Figma link
interface SectionHeaderProps {
  title: string;
  description: string;
  figmaNodeId?: string | null;
  styles: Record<string, string>;
}

export const SectionHeader = ({ title, description, figmaNodeId, styles }: SectionHeaderProps) => (
  <>
    <div className={styles.sectionHeader}>
      <h2 className={styles.sectionTitle}>{title}</h2>
      {figmaNodeId && (
        <a
          href={getFigmaUrl(figmaNodeId)}
          target="_blank"
          rel="noopener noreferrer"
          className={styles.figmaLink}
          title="Open in Figma"
        >
          <FigmaIcon size={18} />
          <span>Open in Figma</span>
        </a>
      )}
    </div>
    <p className={styles.sectionDesc}>{description}</p>
  </>
);

// Sidebar configuration - exported for use in main DemoPage
export const sectionGroups = [
  {
    label: 'Overview',
    items: [{ id: 'pages', label: 'Pages', figmaNodeId: null }],
  },
  {
    label: 'Design Tokens',
    items: [
      { id: 'colors', label: 'Colors', figmaNodeId: '2-462' },
      { id: 'icons', label: 'Icons', figmaNodeId: '6-60' },
      { id: 'typography', label: 'Typography', figmaNodeId: '2-909' },
    ],
  },
  {
    label: 'Components',
    items: [
      { id: 'avatar', label: 'Avatar', figmaNodeId: '808-9932' },
      { id: 'buttons', label: 'Buttons', figmaNodeId: '5-6' },
      { id: 'contact-link', label: 'Contact Link', figmaNodeId: '356-3875' },
      { id: 'empty-states', label: 'Empty States', figmaNodeId: '134-710' },
      { id: 'header', label: 'Header', figmaNodeId: '14-102' },
      { id: 'icon-button', label: 'Icon Button', figmaNodeId: '271-1679' },
      { id: 'info-chip', label: 'Info Chip', figmaNodeId: '616-6450' },
      { id: 'inputs', label: 'Inputs', figmaNodeId: '5-44' },
      { id: 'input-otp', label: 'Input OTP', figmaNodeId: '232-2184' },
      { id: 'lead-type-header', label: 'Lead Type Header', figmaNodeId: '137-1432' },
      { id: 'logo', label: 'Logo', figmaNodeId: '229-1071' },
      { id: 'menu-select', label: 'Menu Select', figmaNodeId: '281-2003' },
      { id: 'modals', label: 'Modals', figmaNodeId: '137-1048' },
      { id: 'generate-more-modal', label: 'Generate More Modal', figmaNodeId: '248-1692' },
      { id: 'gif-preview', label: 'GIF Preview', figmaNodeId: null },
      { id: 'numeric-stepper', label: 'Numeric Stepper', figmaNodeId: '258-1649' },
      { id: 'page-header', label: 'Page Header', figmaNodeId: '137-709' },
      { id: 'profile-card', label: 'Profile Card', figmaNodeId: '361-3974' },
      { id: 'segmented-control', label: 'Segmented Control', figmaNodeId: null },
      { id: 'selectable-row', label: 'Selectable Row', figmaNodeId: '632-15795' },
      { id: 'select-filter', label: 'Select Filter', figmaNodeId: '310-7300' },
      { id: 'sso-buttons', label: 'SSO Buttons', figmaNodeId: '232-2152' },
      { id: 'table-cells', label: 'Table Cells', figmaNodeId: '68-619' },
      { id: 'tabs', label: 'Tabs', figmaNodeId: '6-242' },
      { id: 'theme-switcher', label: 'Theme Switcher', figmaNodeId: '576-4756' },
      { id: 'tooltip', label: 'Tooltip', figmaNodeId: null },
      { id: 'typeahead', label: 'TypeAhead', figmaNodeId: '281-2042' },
      { id: 'whatsapp-card', label: 'WhatsApp Card', figmaNodeId: '790-9120' },
    ],
  },
];

// Type for section IDs
export type SectionId =
  | 'pages'
  | 'colors'
  | 'icons'
  | 'typography'
  | 'avatar'
  | 'buttons'
  | 'contact-link'
  | 'empty-states'
  | 'header'
  | 'icon-button'
  | 'info-chip'
  | 'inputs'
  | 'input-otp'
  | 'lead-type-header'
  | 'logo'
  | 'menu-select'
  | 'modals'
  | 'generate-more-modal'
  | 'numeric-stepper'
  | 'page-header'
  | 'profile-card'
  | 'segmented-control'
  | 'selectable-row'
  | 'select-filter'
  | 'sso-buttons'
  | 'table-cells'
  | 'tabs'
  | 'theme-switcher'
  | 'tooltip'
  | 'typeahead'
  | 'whatsapp-card'
  | 'gif-preview';
